import { useEffect } from 'react';

/**
 * One-time scroll fade-in animation.
 * Elements animate once when they enter the viewport and NEVER replay.
 * Use for: cards, icons, gallery images, fade-ins on all pages.
 * Supports: .scroll-animate | .scroll-animate-left | .scroll-animate-right
 */
export function useScrollAnimation() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('scroll-animate-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    const schedule = requestAnimationFrame(() => {
      const elements = document.querySelectorAll(
        '.scroll-animate, .scroll-animate-left, .scroll-animate-right'
      );
      elements.forEach((el) => observer.observe(el));
    });

    return () => {
      cancelAnimationFrame(schedule);
      observer.disconnect();
    };
  }, []);
}

/**
 * One-time scroll SLIDE animation — elements start off-screen
 * and slide into place when the user scrolls down to them. They stay put.
 * Directions: .scroll-slide-left | .scroll-slide-right | .scroll-slide-up
 *
 * Because elements start translated completely outside the viewport,
 * the IntersectionObserver may not detect them initially. We run an
 * immediate sweep on mount to catch any elements already in view.
 */
export function useScrollSlideAnimation() {
  useEffect(() => {
    const selectors = '.scroll-slide-left, .scroll-slide-right, .scroll-slide-up';

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('scroll-slide-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );

    const initialSweep = () => {
      const allEls = document.querySelectorAll(selectors);
      const vh = window.innerHeight;
      allEls.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < vh && rect.bottom > 0) {
          el.classList.add('scroll-slide-visible');
          observer.unobserve(el);
        } else {
          observer.observe(el);
        }
      });
    };

    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(initialSweep);
    });

    return () => {
      cancelAnimationFrame(raf1);
      observer.disconnect();
    };
  }, []);
}